import {
  PlacedAnnotationSchema,
  SurfaceSchema,
} from '@plangrid/scribble-client';

import {
  fetchResponseWithToken,
  getApigeeUrl,
} from '@adsk/acs-sc-web-platform-api';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';

import {
  RelationshipType,
  RELATIONSHIP_TO_LINK_TYPE,
  THUMBNAIL_SIZE_TYPE,
} from './consts';
import { services } from './sdkServicesMarkup';

export const getThumbnail = async (
  bubbleUrn: any,
  bubbleViewableGuid: any,
  scopes: string[] = [],
  type = THUMBNAIL_SIZE_TYPE.SMALL,
): Promise<string | undefined> => {
  try {
    const path = await services.DerivativeService.getThumbnailByUrn(
      bubbleUrn,
      {
        scopes,
        type,
        guid: bubbleViewableGuid,
      },
      { skipBlob: true },
    );
    const res = await fetchResponseWithToken(`${getApigeeUrl()}${path}`);
    if (res.status !== 200) return undefined;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return undefined;
  }
};

export const chunk = (arr: any[], size: number) => {
  const temp = [...arr];
  const results = [];
  while (temp.length) {
    results.push(temp.splice(0, size));
  }
  return results;
};

export const formatSheetHistory = (sheet: any) => ({
  ...sheet,
  sheetId: sheet.id,
  id: sheet.historyId,
});

export const dateToLocalString = (dateStr?: string, locale?: string) => {
  if (dateStr === undefined) return '';
  return displayDate(new Date(dateStr), {
    format: VALID_DATE_FORMATS.DEFAULT,
    locale,
  });
};

type EntityLinkProps = {
  projectId: string;
  entityId: string;
  markups?: PlacedAnnotationSchema[];
  surface?: SurfaceSchema;
};

const makeInitSearchParams = ({
  moduleId,
  entityId,
  viewableGuid,
  markups,
}: {
  moduleId: string;
  entityId: string;
  viewableGuid?: string | null;
  markups?: PlacedAnnotationSchema[];
}) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (markups) {
    const markupsSelection = {
      selection: {
        ids: markups.map((m) => m.uid),
      },
    };
    if (viewableGuid) {
      urlSearchParams.set(
        'viewableGuid',
        (markups.length > 0 && viewableGuid) || '',
      );
    }

    urlSearchParams.set('markups', JSON.stringify(markupsSelection));
  }

  urlSearchParams.set('moduleId', moduleId);
  urlSearchParams.set('entityId', entityId);
  return urlSearchParams;
};

export const sheetEntityLink = ({
  projectId,
  entityId,
  markups,
}: EntityLinkProps): string => {
  const urlSearchParams = makeInitSearchParams({
    moduleId: '2d',
    entityId,
    markups,
  });
  return `/build/sheets/projects/${projectId}?${urlSearchParams.toString()}`;
};

export const documentEntityLink = ({
  projectId,
  entityId,
  markups,
  surface,
}: EntityLinkProps): string => {
  const urlSearchParams = makeInitSearchParams({
    moduleId: 'folders',
    entityId,
    markups,
    viewableGuid: surface?.viewable_guid,
  });
  return `/build/files/projects/${projectId}?${urlSearchParams.toString()}`;
};

export const filterMarkupsByEntity = (
  markups: PlacedAnnotationSchema[],
  sourceEntityType?: RelationshipType,
  sourceEntityId?: string,
) =>
  markups
    .filter(
      (m: PlacedAnnotationSchema) =>
        m.links &&
        m.links.some(
          (link) =>
            sourceEntityType &&
            link.type === RELATIONSHIP_TO_LINK_TYPE[sourceEntityType] &&
            link.destination_id === sourceEntityId,
        ),
    )
    .sort((a, b) => {
      if (!a.updated_at) return 1;
      if (!b.updated_at) return -1;
      return (
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    });
